import React, { useState, useEffect } from 'react';
import { Box, Paper, Skeleton, Grid, Card, CardContent } from '@mui/material';
import DashboardHeader from './DashboardHeader';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import {
  getEditBookingPageShellSx,
  getEditBookingHeroPaperSx,
  getEditBookingMainPaperSx,
  getEditBookingSectionCardSx,
} from '../../utils/editBookingLayout';

/**
 * Skeleton loader for Edit Booking page.
 * Used for both initial lazy load (Suspense fallback) and data fetch loading state.
 */
const EditBookingPageSkeleton = () => {
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => setCurrentTheme(newTheme));
    return cleanup;
  }, []);


  return (
    <Box sx={getEditBookingPageShellSx(isDark)}>
      <Box sx={{ mb: 3 }}>
        <DashboardHeader
          title="Edit Booking"
          backFallbackPath="/booking/list/"
        />
      </Box>

      {/* Hero Card skeleton */}
      <Paper elevation={0} sx={getEditBookingHeroPaperSx(isDark)}>
        <Box sx={{ px: 2, py: 1.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
            <Skeleton variant="circular" width={40} height={40} sx={{ flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" width={80} height={16} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width={220} height={20} sx={{ mb: 1 }} />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Skeleton variant="rectangular" width={72} height={22} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" width={90} height={22} sx={{ borderRadius: 1 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Main content skeleton */}
      <Paper elevation={0} sx={getEditBookingMainPaperSx(isDark)}>
        <Grid container spacing={3}>
          {/* Left column */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Booking row: date inputs + summary card */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Skeleton variant="rounded" width="100%" height={56} sx={{ borderRadius: 1 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Skeleton variant="rounded" width="100%" height={56} sx={{ borderRadius: 1 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Skeleton variant="text" width={60} height={20} sx={{ mb: 0.5 }} />
                      <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: 2 }} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Skeleton variant="text" width={60} height={20} sx={{ mb: 0.5 }} />
                      <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: 2 }} />
                    </Grid>
                  </Grid>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Card elevation={0} sx={getEditBookingSectionCardSx(isDark)}>
                    <CardContent>
                      <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1.5 }} />
                      <Grid container spacing={1}>
                        {[1, 2, 3, 4].map((i) => (
                          <Grid item xs={6} key={i}>
                            <Skeleton variant="text" width={70} height={16} />
                            <Skeleton variant="text" width={90} height={20} />
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              </Box>

              {/* Amount row */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} variant="rounded" width={120} height={56} sx={{ borderRadius: 1 }} />
                ))}
              </Box>

              {/* Rooms section */}
              <Box>
                <Skeleton variant="text" width={140} height={28} sx={{ mb: 1.5 }} />
                <Skeleton variant="rounded" width="100%" height={120} sx={{ borderRadius: 1 }} />
              </Box>

              {/* Guest section */}
              <Box>
                <Skeleton variant="text" width={120} height={28} sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Skeleton variant="rounded" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rounded" width="100%" height={56} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rounded" width="60%" height={56} sx={{ borderRadius: 1 }} />
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right column - Payment */}
          <Grid item xs={12} lg={4}>
            <Card
              elevation={0}
              sx={{
                ...getEditBookingSectionCardSx(isDark),
                position: 'sticky',
                top: 24,
              }}
            >
              <CardContent>
                <Skeleton variant="text" width={160} height={28} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="text" width="95%" height={24} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: 1, mb: 2 }} />
                <Skeleton variant="text" width={120} height={40} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default EditBookingPageSkeleton;
